const API_URL = process.env.REACT_APP_API_URL

export const ACCOUNTS_MONTH_END_API_URL = `${API_URL}/qhse/month-end-reports/accounts-report`

export type AccountsMonthEndReportLine = {
  id?: number
  inventoryItemCategoryId?: number | null
  inventoryItemCategoryName?: string | null
  inventoryItemSubCategoryId?: number | null
  inventoryItemSubCategoryName?: string | null
  inventoryItemId?: number | null
  itemName?: string | null
  impaCode?: string | null
  unitOfMeasurement?: string | null
  accountCodeId?: number | null
  accountCode?: string | null
  balanceCarriedForward?: number | null
  consumption?: number | null
  bunker?: number | null
  initialCost?: number | null
  rob?: number | null
  consumptionValue?: number | null
  consumptionShortfallQty?: number | null
}

export type AvailableItem = {
  id: number
  itemName: string
  impaCode: string
  unitOfMeasurement: string
  quantity: number
  weightedPrice: number
  lotCount: number
}

export type AllocationDto = {
  id?: number
  reportId?: number
  reportLineId?: number
  stockLotId?: number
  poNumber?: string | null
  lotReceivedDate?: string | null
  allocatedQty?: number
  unitCost?: number | null
  allocatedValue?: number | null
}

export type LineConsumptionDto = {
  reportLineId: number
  totalAllocatedQty: number
  totalValue: number
  allocations: AllocationDto[]
}

export type AccountsMonthEndReport = {
  id: number
  companyId?: number | null
  companyType?: string | null
  companyName?: string | null
  vesselId?: number | null
  vesselName?: string | null
  reportYear?: number | null
  reportMonth?: number | null
  remarks?: string | null
  totalBalanceCarriedForward?: number | null
  totalConsumption?: number | null
  totalBunker?: number | null
  totalInitialCost?: number | null
  totalRob?: number | null
  totalConsumptionValue?: number | null
  createdByName?: string | null
  createdDate?: string | null
  lines?: AccountsMonthEndReportLine[]
}

/** Account code as booked on a purchase order for one inventory item. */
export type ResolvedAccountCode = {
  accountCodeId: number | null
  accountCode: string | null
}

/**
 * The backend reports failures as {status, error, message}. Surfacing `message` matters here
 * because the duplicate-month rule is only explained in it — a generic "failed to save" would
 * hide the reason the user needs.
 */
export const extractApiError = async (response: Response, fallback: string) => {
  try {
    const body = await response.json()
    return body?.message || body?.error || fallback
  } catch {
    return fallback
  }
}

/**
 * Account codes are never chosen by the user: they are derived from the purchase orders raised for
 * the vessel. Returns a map of inventory item id -> resolved code for the requested items.
 */
export const fetchAccountCodesForItems = async (
  jwt: string,
  vesselId: number | string,
  inventoryItemIds: Array<number | string>
): Promise<Record<string, ResolvedAccountCode>> => {
  const ids = inventoryItemIds.filter((id) => id !== '' && id != null)
  if (!vesselId || ids.length === 0) return {}

  const params = new URLSearchParams()
  ids.forEach((id) => params.append('inventoryItemIds', String(id)))

  const response = await fetch(
    `${ACCOUNTS_MONTH_END_API_URL}/account-codes/${vesselId}?${params.toString()}`,
    {headers: {Authorization: `Bearer ${jwt}`}}
  )
  if (!response.ok) return {}
  const payload = await response.json()
  return payload && typeof payload === 'object' ? payload : {}
}

/** True when a report already exists for this vessel/month — one report per month is the rule. */
export const checkMonthTaken = async (
  jwt: string,
  vesselId: number | string,
  year: number,
  month: number,
  excludeId?: number | null
): Promise<boolean> => {
  if (!vesselId || !year || !month) return false
  const params = new URLSearchParams({
    vesselId: String(vesselId),
    year: String(year),
    month: String(month),
  })
  if (excludeId != null) params.set('excludeId', String(excludeId))

  const response = await fetch(`${ACCOUNTS_MONTH_END_API_URL}/exists?${params.toString()}`, {
    headers: {Authorization: `Bearer ${jwt}`},
  })
  if (!response.ok) return false
  const payload = await response.json()
  return Boolean(payload?.exists)
}

/** Company group and company-admin ids share one dropdown, so the type must ride along. */
export const companyOptionValue = (company: {id: number; type: string}) =>
  `${company.type}:${company.id}`

/** Keep only characters valid for a decimal amount so pasted junk cannot reach the DTO. */
export const sanitizeNumeric = (value: string) => {
  const cleaned = value.replace(/[^\d.]/g, '')
  const [whole, ...rest] = cleaned.split('.')
  return rest.length ? `${whole}.${rest.join('').slice(0, 4)}` : whole
}

export const toNumber = (value: string) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

export const formatAmount = (value: number) => value.toFixed(2)

export const resolveItemName = (item: any) =>
  item?.itemName || item?.manualItemName || item?.part?.name || item?.subcat?.name || `Item #${item?.id}`

const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

export const formatReportMonth = (year?: number | null, month?: number | null) => {
  if (!year || !month || month < 1 || month > 12) return '-'
  return `${MONTH_LABELS[month - 1]} ${year}`
}

export const sumLines = (
  lines: AccountsMonthEndReportLine[] | undefined,
  key: keyof AccountsMonthEndReportLine
) =>
  (lines || []).reduce((total, line) => {
    const raw = Number(line[key])
    return total + (Number.isFinite(raw) ? raw : 0)
  }, 0)

export const fetchAvailableItems = async (
  jwt: string,
  vesselId: number | string,
  subCategoryId?: number | string | null
): Promise<AvailableItem[]> => {
  if (!vesselId) return []
  const params = new URLSearchParams()
  if (subCategoryId) {
    params.set('inventoryItemSubCategoryId', String(subCategoryId))
  }
  const url = `${ACCOUNTS_MONTH_END_API_URL}/stock/${vesselId}/available-items?${params.toString()}`
  const response = await fetch(url, {
    headers: {Authorization: `Bearer ${jwt}`},
  })
  if (!response.ok) return []
  return response.json()
}

export const fetchLineAllocations = async (
  jwt: string,
  reportLineId: number | string
): Promise<LineConsumptionDto | null> => {
  if (!reportLineId) return null
  const response = await fetch(
    `${ACCOUNTS_MONTH_END_API_URL}/lines/${reportLineId}/stock-allocations`,
    {headers: {Authorization: `Bearer ${jwt}`}}
  )
  if (!response.ok) return null
  return response.json()
}
